<template>
  <div>
    <MainHeader title="End User License">
      <template #left>
        <font-awesome-icon
          :icon="['fas', 'arrow-left-long']"
          class="pointer"
          @click="router.push({ name: 'Legal' })"
        />
      </template>
    </MainHeader>

    <div class="view legal-doc-view">
      <div class="wrapper-card legal-doc-card">
        <div class="legal-doc-header">
          <h1 class="legal-doc-title">End User License Agreement (EULA)</h1>
          <div class="legal-doc-dates">
            <span class="legal-date-badge">Effective: April 9, 2025</span>
            <span class="legal-date-badge">Updated: May 19, 2025</span>
          </div>
        </div>

        <p class="legal-body">
          Please read this End User License Agreement ("Agreement") carefully
          before downloading or using the Lucky Friday mobile or web application
          ("App"). By accessing or using the App, you agree to be bound by the
          terms of this Agreement. If you do not agree, do not use the App.
        </p>

        <div class="legal-section">
          <h3 class="legal-section-title">1. License Grant</h3>
          <p class="legal-body">
            Lucky Friday LLC ("we," "our," or "us") grants you a limited,
            non-exclusive, non-transferable, non-sublicensable, revocable
            license to install and use the App solely for your personal,
            non-commercial use in accordance with this Agreement.
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">2. Restrictions</h3>
          <p class="legal-body">You agree not to:</p>
          <ul class="legal-list">
            <li>
              Copy, modify, adapt, translate, reverse engineer, disassemble, or
              decompile the App
            </li>
            <li>
              Use the App for any commercial purpose without express written
              consent
            </li>
            <li>
              Rent, lease, sublicense, sell, or distribute the App or any part
              of it
            </li>
            <li>
              Use the App to engage in any unlawful or fraudulent activity
            </li>
            <li>
              Bypass or circumvent any security or authentication measures in
              the App
            </li>
          </ul>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">3. Ownership</h3>
          <p class="legal-body">
            The App is licensed, not sold. All rights, title, and interest in
            and to the App (including all source code, user interface, design,
            trademarks, and content) are and remain the property of Lucky Friday
            LLC or its licensors.
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">4. Privacy</h3>
          <p class="legal-body">
            Your use of the App is subject to our Privacy Policy, which outlines
            how we collect, use, and protect your information. By using the App,
            you consent to the practices described in the Privacy Policy.
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">5. Third-Party Services</h3>
          <p class="legal-body">
            The App may integrate with third-party services, including Plaid
            Inc. You agree to any applicable third-party terms when using those
            features. We are not responsible for third-party content or
            services.
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">6. Updates</h3>
          <p class="legal-body">
            We may provide updates, bug fixes, or enhancements to the App. These
            updates may be installed automatically or may require manual
            installation. Your continued use of the App after such updates
            constitutes acceptance of the changes.
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">7. Termination</h3>
          <p class="legal-body">
            This license is effective until terminated. We may suspend or
            terminate your access to the App at any time for violation of this
            Agreement or applicable laws. Upon termination, you must stop all
            use of the App and uninstall it from your devices.
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">8. Disclaimer of Warranties</h3>
          <p class="legal-body">
            The App is provided "as is" and "as available" without warranties of
            any kind. We do not guarantee that the App will be error-free,
            uninterrupted, or secure. You use the App at your own risk.
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">9. Limitation of Liability</h3>
          <p class="legal-body">
            To the fullest extent permitted by law, Lucky Friday LLC shall not
            be liable for any indirect, incidental, special, consequential, or
            punitive damages, including loss of data, loss of profits, or other
            losses, even if advised of the possibility of such damages.
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">10. Governing Law</h3>
          <p class="legal-body">
            This Agreement is governed by and construed in accordance with the
            laws of the State of Idaho, without regard to its conflict of law
            principles.
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">11. Contact</h3>
          <p class="legal-body">
            If you have any questions about this Agreement, please contact us
            at:
          </p>
          <a
            href="mailto:support@luckyfridayapp.com"
            class="legal-contact-link"
          >
            <font-awesome-icon
              :icon="['fas', 'envelope']"
              style="margin-right: 6px"
            />
            support@luckyfridayapp.com
          </a>
          <p class="legal-body mt-half">
            <font-awesome-icon
              :icon="['fas', 'location-dot']"
              style="margin-right: 6px; color: #aaa"
            />
            Boise, Idaho, USA
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MainHeader from '../../../components/View-Header.vue';
import { useRouter } from 'vue-router';

export default {
  name: 'EndUserLicense',
  components: { MainHeader },
  setup() {
    const router = useRouter();
    return { router };
  },
};
</script>

<style scoped>
.legal-doc-view {
  max-width: 600px;
  margin: 0 auto;
}

.legal-doc-card {
  animation: cardSlideUp 0.4s cubic-bezier(0.34, 1.2, 0.64, 1) both;
  padding: 1.5rem;
}

.legal-doc-header {
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1.5px solid #eeedfe;
}
.dark-theme .legal-doc-header {
  border-bottom-color: #2a3a4e;
}

.legal-doc-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #004751;
  margin: 0 0 0.6rem;
  letter-spacing: -0.01em;
  line-height: 1.3;
}
.dark-theme .legal-doc-title {
  color: white;
}

.legal-doc-dates {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.legal-date-badge {
  font-size: 0.7rem;
  font-weight: 600;
  background: var(--purple-50);
  color: var(--color-accent-purple);
  padding: 2px 10px;
  border-radius: 20px;
}
.dark-theme .legal-date-badge {
  background: #1e1a40;
}

.legal-section {
  margin-bottom: 1.25rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid #f0f0f0;
}
.dark-theme .legal-section {
  border-bottom-color: #2a3a4e;
}
.legal-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.legal-section-title {
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--color-accent-purple);
  margin: 0 0 0.6rem;
}
.dark-theme .legal-section-title {
  color: var(--color-accent-teal);
}

.legal-body {
  font-size: 0.82rem;
  color: #515a65;
  line-height: 1.65;
  margin: 0 0 0.6rem;
}
.dark-theme .legal-body {
  color: #d4d2d2;
}

.legal-list {
  font-size: 0.82rem;
  color: #515a65;
  line-height: 1.65;
  padding-left: 1.25rem;
  margin: 0.4rem 0;
}
.dark-theme .legal-list {
  color: #d4d2d2;
}
.legal-list li {
  margin-bottom: 0.5rem;
}
.legal-list li:last-child {
  margin-bottom: 0;
}

.legal-contact-link {
  display: inline-flex;
  align-items: center;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--color-accent-purple);
  text-decoration: none;
  background: var(--purple-50);
  padding: 0.5rem 1rem;
  border-radius: var(--radius-pill);
  transition: background var(--transition-fast);
  margin-bottom: 0.5rem;
}
.legal-contact-link:hover {
  background: var(--purple-100);
}
.dark-theme .legal-contact-link {
  background: #1e1a40;
  color: var(--color-accent-teal);
}

.mt-half {
  margin-top: 0.5rem;
}
</style>

<!-- <template>
  <div>
    <MainHeader title="End User License">
      <template #left>
        <font-awesome-icon
          :icon="['fas', 'arrow-left-long']"
          class="pointer"
          @click="router.push({ name: 'Legal' })"
        />
      </template>
    </MainHeader>

    <div class="view support-page">
      <div class="wrapper-card closing-text ps-2 pe-2 small-grey-text">
        <div>
          <h3>Lucky Friday End User License Agreement (EULA)</h3>
          <h4>Effective Date: April 9, 2025</h4>
          <h4>Last Updated: May 19, 2025</h4>

          <p>
            Please read this End User License Agreement ("Agreement") carefully
            before downloading or using the Lucky Friday mobile or web
            application ("App"). By accessing or using the App, you agree to be
            bound by the terms of this Agreement. If you do not agree, do not
            use the App.
          </p>

          <h4>1. License Grant</h4>
          <p>
            Lucky Friday LLC (“we,” “our,” or “us”) grants you a limited,
            non-exclusive, non-transferable, non-sublicensable, revocable
            license to install and use the App solely for your personal,
            non-commercial use in accordance with this Agreement.
          </p>

          <h4>2. Restrictions</h4>
          <p>You agree not to:</p>
          <ul>
            <li>
              Copy, modify, adapt, translate, reverse engineer, disassemble, or
              decompile the App
            </li>
            <li>
              Use the App for any commercial purpose without express written
              consent
            </li>
            <li>
              Rent, lease, sublicense, sell, or distribute the App or any part
              of it
            </li>
            <li>
              Use the App to engage in any unlawful or fraudulent activity
            </li>
            <li>
              Bypass or circumvent any security or authentication measures in
              the App
            </li>
          </ul>

          <h4>3. Ownership</h4>
          <p>
            The App is licensed, not sold. All rights, title, and interest in
            and to the App (including all source code, user interface, design,
            trademarks, and content) are and remain the property of Lucky Friday
            LLC or its licensors.
          </p>

          <h4>4. Privacy</h4>
          <p>
            Your use of the App is subject to our Privacy Policy, which outlines
            how we collect, use, and protect your information. By using the App,
            you consent to the practices described in the Privacy Policy.
          </p>

          <h4>5. Third-Party Services</h4>
          <p>
            The App may integrate with third-party services, including Plaid
            Inc. You agree to any applicable third-party terms when using those
            features. We are not responsible for third-party content or
            services.
          </p>

          <h4>6. Updates</h4>
          <p>
            We may provide updates, bug fixes, or enhancements to the App. These
            updates may be installed automatically or may require manual
            installation. Your continued use of the App after such updates
            constitutes acceptance of the changes.
          </p>

          <h4>7. Termination</h4>
          <p>
            This license is effective until terminated. We may suspend or
            terminate your access to the App at any time for violation of this
            Agreement or applicable laws. Upon termination, you must stop all
            use of the App and uninstall it from your devices.
          </p>

          <h4>8. Disclaimer of Warranties</h4>
          <p>
            The App is provided "as is" and "as available" without warranties of
            any kind. We do not guarantee that the App will be error-free,
            uninterrupted, or secure. You use the App at your own risk.
          </p>

          <h4>9. Limitation of Liability</h4>
          <p>
            To the fullest extent permitted by law, Lucky Friday LLC shall not
            be liable for any indirect, incidental, special, consequential, or
            punitive damages, including loss of data, loss of profits, or other
            losses, even if advised of the possibility of such damages.
          </p>

          <h4>10. Governing Law</h4>
          <p>
            This Agreement is governed by and construed in accordance with the
            laws of the State of Idaho, without regard to its conflict of law
            principles.
          </p>

          <h4>11. Contact</h4>
          <p>
            If you have any questions about this Agreement, please contact us
            at:
          </p>
          <p>
            📧
            <a href="mailto:support@luckyfridayapp.com">support@luckyfridayapp.com</a
            ><br />
            📍 Boise, Idaho, USA
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MainHeader from '../../../components/View-Header.vue';
import { useRouter } from 'vue-router';

export default {
  name: 'User-Support',
  components: {
    MainHeader,
  },
  setup() {
    const router = useRouter();

    return {
      router,
    };
  },
};
</script>
<style scoped>
a {
  color: black;
}
</style> -->
